import {makeStyles, useTheme} from "@material-ui/core";

import {Identifier, ListContextProvider, useList, useListContext,} from "ra-core";
import {useEffect, useMemo, useState} from "react";
import {Datagrid, NumberField, Record} from "react-admin";
import {Group} from "../../types";
import _ from "lodash";
import {ChargesDatagrid} from "./ChargesDatagrid";
import {SubjectField} from "../../../components/SubjectField";
import {CustomPagination} from "../../../components/CustomPagination";

const useStyles = makeStyles((theme) => ({
  headerCell: {
    backgroundColor: "white",
    color: theme.palette.secondary.dark,
  },
}));

interface ClientGroupedChargeDatagridProps {
  setActiveCharge: (charge: Record | undefined) => void;
  activeCharge?: Record;
  rowStyle: (record: Record, index: number) => any;
  Actions: React.ElementType;
}

export const ClientGroupedChargeDatagrid = (
  props: ClientGroupedChargeDatagridProps
) => {
  const { setActiveCharge, rowStyle, Actions, activeCharge } = props;
  const { ids, data, loading, loaded } = useListContext();
  const classes = useStyles();
  const [groupedData, setGroupedData] = useState<Group[]>([]);
  const [selectedCharges, setSelectedCharges] = useState<Identifier[]>([]);
  const listContext = useList({
    data: groupedData,
    ids: groupedData.map((group) => group.id),
    loading: loading,
    loaded: loaded,
    perPage: 2000
  });
  const theme = useTheme();
  useEffect(() => {
    const listData = ids.map((id) => data[id]);

    const groupCharges = (data: Record[]) => {
      const groupedData: Group[] = [];
      let color = "white";
      const groupedByClient = _.groupBy<Record>(data, "client_id");
      _.forEach(groupedByClient, function (value, clientKey) {
        const client_charges = {
          supplier: null,
          client: value[0]["client"],
          id: clientKey,
          charges: value
            .sort((a: Record, b: Record) => {
              if (a.main_delivery_id < b.main_delivery_id) {
                return -1;
              }
              if (a.main_delivery_id > b.main_delivery_id) {
                return 1;
              }
              // a must be equal to b
              return 0;
            })
            .map((charge, index, data) => {
              if (
                index - 1 >= 0 &&
                data[index - 1].main_delivery_id !== charge.main_delivery_id
              ) {
                color = color === "white" ? theme.palette.grey[200] : "white";
              }
              return { ...charge, color };
            }),
          total: value.reduce((sum, charge) => sum + charge.taxable, 0),
        };
        groupedData.push(client_charges);
      });
      return groupedData;
    };
    setGroupedData(groupCharges(listData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ids]);

  const onSelect = (ids: Identifier[]) => {
    setSelectedCharges(
      ids.reduce((chargeIdList, id) => {
        listContext.data[id].charges.forEach((charge: Record) =>
          chargeIdList.push(charge.id)
        );
        return chargeIdList;
      }, [] as Identifier[])
    );
  };

  const onToggleItem = (id: Identifier) => {
    const chargesIdClient = ids.reduce((chargeIdList, chargeId) => {
      if (data[chargeId].client_id.toString() === id.toString())
        chargeIdList.push(chargeId);
      return chargeIdList;
    }, [] as Identifier[]);
    const inSelectedCharges = chargesIdClient.every((id) =>
      selectedCharges.includes(id)
    );
    setSelectedCharges((old) => {
      const newSelectedCharges = inSelectedCharges
        ? old.filter((chargeId) => !chargesIdClient.includes(chargeId))
        : [...old, ...chargesIdClient];
      return newSelectedCharges;
    });
  };

  const selectedClients = useMemo(
    () =>
      listContext.ids.filter((id) => {
        const record = listContext.data[id] as Group;
        return record.charges.every((charge) =>
          selectedCharges.includes(charge.id)
        );
      }),
    [listContext.data, listContext.ids, selectedCharges]
  );
  useEffect(() => {
    const getNext = (id: Identifier, charges: Record[]) => {
      const index = charges.findIndex((charge) => charge.id === id);
      const nextIndex = index + 1 < charges.length ? index + 1 : index;
      return charges[nextIndex];
    };

    let activeCharge: Record | undefined = undefined;
    if (selectedCharges.length > 0) {
      const lastSelectedCharge = selectedCharges[selectedCharges.length - 1];
      const charges = listContext.ids.reduce((charges, id) => {
        charges.push(...listContext.data[id].charges);
        return charges;
      }, [] as Record[]);

      activeCharge = getNext(lastSelectedCharge, charges);
    }
    setActiveCharge && setActiveCharge(activeCharge);
  }, [listContext.data, listContext.ids, selectedCharges, setActiveCharge]);

  return (
    <ListContextProvider value={listContext}>
      <Actions
        selectedCharges={selectedCharges}
        onUnselect={() => onSelect([])}
      />

      <Datagrid
        hasBulkActions
        onSelect={onSelect}
        classes={classes}
        selectedIds={selectedClients}
        onToggleItem={onToggleItem}
        expand={
          <ChargesDatagrid
            selectedCharges={selectedCharges}
            setSelectedCharges={setSelectedCharges}
            setActiveCharge={setActiveCharge}
            rowStyle={rowStyle}
            activeCharge={activeCharge}
          />
        }
      >
        <SubjectField sortable={false} source="client" label="Cliente" />
        <NumberField
          sortable={false}
          source="total"
          label="Totale imponibile"
        />
      </Datagrid>
      <CustomPagination />
    </ListContextProvider>
  );
};
